import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './base';

/**
 * Merkezi (tek) RTK Query API instance'ı.
 *
 * Tüm endpoint dosyaları artık ayrı `createApi` çağırmak yerine bu `rootApi`
 * üzerine `rootApi.injectEndpoints(...)` ile enjekte edilir. Böylece:
 *  - store yalnızca tek bir reducer + tek bir middleware kaydeder,
 *  - modül bağımlılık grafiği düzleşir (maksimum call stack sorununu çözer),
 *  - yeni endpoint eklemek mevcut yapıyı bozmaz.
 *
 * NOT: `injectEndpoints` ile yeni `tagTypes` eklenemez. Bu yüzden projedeki
 * TÜM tag tipleri burada merkezi olarak tanımlanmıştır. Yeni bir tag tipi
 * gerektiğinde bu listeye eklenmelidir.
 *
 * Geliştirici rehberi: docs/RTK_QUERY_API.md
 */
export const rootApi = createApi({
    reducerPath: 'rootApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        // auth
        'AuthDetails',
        // announcement
        'Announcement',
        'AnnouncementDocument',
        'AnnouncementType',
        // brand / model / product
        'Brand',
        'Model',
        'ProductGroup',
        // customer
        'Customer',
        'CustomerDeclarationList',
        'CustomerDeclarationDetail',
        // dealer
        'Dealer',
        'DealerAddress',
        'DealerAddressBrand',
        'DealerAddressBrandPayment',
        'DealerAddressBrandProfit',
        'DealerAddressBrandAuthSrcType',
        'DealerAddressBrandRegionalManager',
        'DealerAdressBrandTechnicalSupportResponsible',
        'DealerAddressBrandAddrReport',
        'DealerAddressOrderType',
        'DealerAddressPriceList',
        'DealerAddressUsers',
        'DealerAgreementType',
        'DealerRegistration',
        'DealerApproval',
        'DealerOtherLaborPrice',
        'DealerPartsReturnProcess',
        'DealerStockAdd',
        'DealerStock',
        'DealerStockLog',
        'DealersStockView',
        'DealerWarehouse',
        // user
        'UserGroup',
        'UserParams',
        'UserAccounts',
        'UserAccount',
        'UserList',
        'RoleList',
        // yetki tabanlı yetkilendirme (Identity)
        'PermissionCatalog',
        'IdentityRoles',
        'IdentityRolePermissions',
        'IdentityRoleSettings',
        'AssignableRoles',
        'UserModuleRoles',
        'UserEffectivePermissions',
        'MyPermissions',
        'AdministrationPages',
        'SuperAdminDefinition',
        // warranty
        'WarrantyClaims',
        'WarrantyClaimsDocuments',
        'WarrantyClaimsItems',
        'WarrantyClaimsLabor',
        'WarrantyClaimsOtherCost',
        'WarrantyClaimsTotals',
        'WarrantyClaimVideos',
        'WarrantyDashboard',
        'WarrantyReports',
        'WarrantyPeriod',
        'VehicleWarrantyPeriod',
        'VehicleWarrantyPeriodDocument',
        'VehicleWarrantyInformation',
        'VehicleWarrantyClaims',
        'VehicleWarrantyClaimsReportSum',
        'WarrantyAudit',
        'WarrantyAuditResult',
        'FormDateChangeRequest',
        // vehicle
        'VehicleItems',
        'VehicleDetailInformation',
        'VehicleRegistrationForm',
        'VehicleRegistrationFormList',
        'VehicleAcceptanceForm',
        'VehicleAcceptanceFormDocuments',
        'VehicleServiceReport',
        'VehicleParkReport',
        'VehicleOrder',
        'VehicleSale',
        'VehicleSaleListing',
        'VehicleSaleRequest',
        'VehicleSaleReport',
        'VehicleRefundOrder',
        'VehicleServiceReport',
        // pre-stock / pre-approval / pre-accounting
        'PreStockInspection',
        'PreApproval',
        'PreApprovalDocument',
        'PreAccounting',
        'PreAccountingCustomers',
        'PreAccountingSelectedTypes',
        'CollateralReceived',
        // approval
        'ApprovalLimits',
        'ApprovalLimitGroups',
        'ApprovalOrders',
        // invoice
        'InvoiceApprovals',
        'InvoiceApproval',
        'InvoiceApprovalHistory',
        'AvailableDocuments',
        'DealerInvoiceHistory',
        'InvoiceDocuments',
        'InvoiceDetail',
        'AllGTOrders',
        'FullServiceInvoice',
        'FullServiceKm',
        'ProformaInvoiceList',
        'ProformaInvoiceDetail',
        'ProformaCustomers',
        // fault / labor / package
        'FaultCodes',
        'FaultClientDefinitions',
        'LaborCatalog',
        'DealerPackages',
        // reports
        'ClaimHistoryReport',
        'PreApprovalHistoryReport',
        'DealerProcessingReport',
        'AccountingReport',
        'CurrentAccountReport',
        'CollectionReport',
        'ServiceBulletinReport',
        'RepairSlipReport',
        'BulletinImplementationReport',
        'MatnrIntegrationStatusReport',
        'MaterialCatalog',
        'VehicleDistributionReport',
        // lms
        'LmsLookupTranslation',
        'LmsTrainingCatalog',
        'LmsTrainingInstructor',
        'LmsTrainingContentOutput',
        'LmsPrecondition',
        'LmsTrainingPrecondition',
        'LmsCatalogPlanning',
        'LmsPlanningInstructor',
        'LmsVideo',
        'LmsTrainingReport',
        'LmsTrainingReportCost',
        'LmsTrainingReportParticipant',
        'LmsTrainingReportInstructor',
        'LmsVehicleTrainingMatch',
        'LmsDocument',
        'LmsNews',
        // lookups / common
        'Compound',
        'Translation',
        'Language',
        'ClaimLimit',
        'UserFavorite',
        'Basket',
        'Country',
        'Chassis',
        'Lookup',
        'PartsDealerBrandMatch',
        'UserTablePreference',
        'CreditCardPayment',
        'TechnicalSupportRequest',
        'TechnicalSupportRequestDocument',
        'TechnicalSupportFeedbackFormReport',
        'CommentDocument',
        'ExpenseDocument',
        'DocumentContent',
        'BulletinDocuments',
        'Orders',
        'ExternalVehicle',
        'ShipmentDelivery',
        // sap
        'Sap',
        'SapMeasure',
        'SapExchangeRate',
        // parts catalog
        'PartDefinitions',
        'PriceInformation',
        'PriceHistory',
        'StockInformation',
        'StockHistory',
        'StockByPartCode',
        // parts
        'WaybillShipment',
        'OrderBalance',
        'PartSearch',
        'GetSapInvoices',
        'getSapInvoiceDetailQuery',
        'InternalOrderPickup',
        'ExternalInvoices',
        'ExternalInvoiceDetail',
        'ExternalOrderPickup',
        'DeliveryNotePickupList',
        'DeliveryNotePickupDetail',
        'DeliveryNotePickup',
        'ExternalItemDescription',
        'RetailList',
        'RetailDetail',
        'RetailPendingList',
        'RetailPendingCancelList',
        'InternalRetailPartsList',
        'InternalRetailPartsDetail',
        'UnpickedParts',
        'VehicleItems',
        'MatnrPartsPriceReport',
        //team-productivity
        'TeamProductivityMeasurement',
        // Kod içinde provides ile kullanılan ancak eski ayrı slice'larda
        // tagTypes'a eklenmemiş olan ek tag tipleri (geriye dönük uyumluluk)
        'PartHistory',
        'CustomerStatementDocuments',
        'CustomerDeclaration',
        'DraftWarrantyClaims',
        'LaborCatalogReport',
        'AvailableWarrantyTypes',
        'ExportedCars',
        'int'
    ],
    endpoints: () => ({})
});
